import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { Grid, Checkbox, FormControlLabel } from '@mui/material';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import MultilineEditor from '../../../../components/common/inputs/MultilineEditor';

const DocumentClauseDetailComponent = ({ clause, dispatch, clauseTypes, parentClauses, documentTypes, validation }) => {
  const { t } = useTranslation();

  const onPropertyChanged = (itemPropertyName, itemValue) => {
    dispatch({ type: 'OnItemValuesChanged', itemPropertyName, itemValue });
  };

  return (
    <Fragment>
      <Prompt when={clause.item.changed && !clause.item.saving} message={t('Clause.LeavingWithoutSavingClause')} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            isNumeric
            fullWidth
            value={clause.item.orderNo}
            decimalScale={0}
            label={t('Clause.Item.OrderNo')}
            onChange={event => onPropertyChanged('orderNo', event)}
            error={!isValid(validation.orderNo)}
            helperText={getErrors(validation.orderNo)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            value={clause.item.clauseCode || ''}
            onChange={event => onPropertyChanged('clauseCode', event)}
            label={t('Clause.Item.ClauseCode')}
            error={!isValid(validation.clauseCode)}
            helperText={getErrors(validation.clauseCode)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            value={clause.item.clauseText || ''}
            onChange={event => onPropertyChanged('clauseText', event)}
            label={t('Clause.Item.ClauseText')}
            error={!isValid(validation.clauseText)}
            helperText={getErrors(validation.clauseText)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            fullWidth
            value={clause.item.clauseTypeId}
            simpleValue
            options={clauseTypes}
            onChange={event => onPropertyChanged('clauseTypeId', event)}
            label={t('Clause.Item.ClauseType')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            fullWidth
            value={clause.item.documentTypeId}
            simpleValue
            options={documentTypes}
            onChange={event => onPropertyChanged('documentTypeId', event)}
            label={t('Clause.Item.DocumentType')}
            error={!isValid(validation.documentTypeId)}
            helperText={getErrors(validation.documentTypeId)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            fullWidth
            value={{ id: clause.item.parentClauseId }}
            simpleValue
            options={parentClauses}
            onChange={event => onPropertyChanged('parentClauseId', event)}
            label={t('Clause.Item.ParentClause')}
            isSearchable={true}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            isNumeric
            fullWidth
            value={clause.item.validityDays}
            decimalScale={0}
            label={t('Clause.Item.ValidityDays')}
            onChange={event => onPropertyChanged('validityDays', event)}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={clause.item.enabled || false}
                onChange={event => onPropertyChanged('enabled', event.target.checked)}
                color='primary'
              />
            }
            label={t('Clause.Item.Enabled')}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={clause.item.mandatory || false}
                onChange={event => onPropertyChanged('mandatory', event.target.checked)}
                color='primary'
              />
            }
            label={t('Clause.Item.Mandatory')}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <MultilineEditor
            formulaLabel={t('Clause.Item.Formula')}
            formulaPropertyName='formula'
            formulaPropertyValue={clause.item.formula || ''}
            isReadOnly={false}
            onPropertyChanged={onPropertyChanged}
          ></MultilineEditor>
        </Grid>
      </Grid>
    </Fragment>
  );
};

DocumentClauseDetailComponent.propTypes = {
  dispatch: PropTypes.func.isRequired,
  clause: PropTypes.object.isRequired,
  clauseTypes: PropTypes.array.isRequired,
  parentClauses: PropTypes.array.isRequired,
  documentTypes: PropTypes.array.isRequired,
  validation: PropTypes.object.isRequired
};

export default DocumentClauseDetailComponent;
